<script lang="ts">
  import { setContext } from 'svelte';
  import clsx from 'clsx';
  import { ArrowBack, ArrowForward } from '@athena-lib/Icons';
  import { scale, blur } from 'svelte/transition';
  import { getResponsiveClasses } from '@athena-lib/responsive-utils';
  import { gaps } from '@athena-lib/spacing';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { contextKey, type CarouselContext, type FullwidthCarouselProps } from './types';
  let scrollList: HTMLOListElement | undefined = $state(undefined);

  const translations = getCommonTranslations();

  const {
    children,
    space = 'gutter',
    class: className,
    action = () => undefined,
    actionArg,
  }: FullwidthCarouselProps = $props();
  let canGoBack = $state(false);
  let canGoForward = $state(true);

  const context: CarouselContext = {
    peekNext: () => true,
  };
  setContext(contextKey, context);

  let timer: ReturnType<typeof setTimeout> | undefined = undefined;
  function onScroll() {
    clearTimeout(timer);
    timer = setTimeout(function () {
      if (!scrollList) {
        return;
      }

      const containerRect = scrollList.getBoundingClientRect();

      canGoBack = scrollList.scrollLeft > 0;
      canGoForward = scrollList.scrollLeft + containerRect.width + 10 < scrollList.scrollWidth;
    }, 50);
  }

  function scroll(direction: 'back' | 'forward') {
    if (!scrollList) {
      return;
    }

    const scrollWidth = scrollList.children[0].getBoundingClientRect().width;
    if (direction === 'back') {
      scrollList.scrollTo({ left: scrollList.scrollLeft - scrollWidth });
    } else {
      scrollList.scrollTo({ left: scrollList.scrollLeft + scrollWidth });
    }
  }

  function elementAction(node: HTMLDivElement) {
    scrollList!.addEventListener('scroll', onScroll);
    if (action) {
      return action(node, actionArg!);
    }
  }
</script>

<div
  use:elementAction
  class="group relative"
  aria-label="Content carousel. Use Tab to explore cards, or use arrow buttons to scroll."
  role="region"
  aria-live="polite"
>
  <ol
    bind:this={scrollList}
    class={clsx(
      className,
      getResponsiveClasses(space, gaps),
      'flex snap-x snap-mandatory overflow-x-scroll scroll-smooth scrollbar-none',
    )}
    aria-label="Carousel"
  >
    {@render children()}
  </ol>
  {#if canGoBack}
    <div class="absolute left-4 top-0 hidden h-full w-10 md:block" in:scale out:scale>
      <button
        type="button"
        class="h-full w-10 opacity-0 transition-opacity duration-300 group-hover:opacity-100 md:w-12 lg:w-14"
        onclick={() => scroll('back')}
        aria-label={translations.previous}
      >
        <div class="w-full rounded-full bg-st-black p-2 text-st-white opacity-60 hover:opacity-80">
          <ArrowBack />
        </div>
      </button>
    </div>
  {/if}
  {#if canGoForward}
    <div
      class="absolute right-8 top-0 hidden h-full w-10 md:block"
      in:blur={{ duration: 1000 }}
      out:blur
    >
      <button
        type="button"
        class="h-full w-10 opacity-0 transition-opacity duration-300 group-hover:opacity-100 md:w-12 lg:w-14"
        onclick={() => scroll('forward')}
        aria-label={translations.next}
      >
        <div class="w-full rounded-full bg-st-black p-2 text-st-white opacity-60 hover:opacity-80">
          <ArrowForward />
        </div>
      </button>
    </div>
  {/if}
</div>
